import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  doc,
  Firestore,
  getCountFromServer,
  onSnapshot,
  query,
  QueryConstraint,
  serverTimestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  constructor(private firestore: Firestore) {}

  count(path: string, ...constraints: QueryConstraint[]): Promise<number> {
    return getCountFromServer(query(collection(this.firestore, path), ...constraints)).then((countSnapshot) => countSnapshot.data().count);
  }

  create<T>(path: string, data: T): Promise<string> {
    return addDoc(collection(this.firestore, path), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }).then((document) => document.id);
  }

  createId(path: string): string {
    return doc(collection(this.firestore, path)).id;
  }

  doc<T>(path: string): Observable<T | null> {
    return new Observable((subscriber) => {
      const unsubscribe = onSnapshot(doc(this.firestore, path), (document) => {
        if (!document.exists() || !document.data()) {
          subscriber.next(null);
          return;
        }
        const data = document.data();
        subscriber.next({
          ...this.changeTimestampsToDate(data),
          id: document.id,
        } as T);
      }, (error) => {
        subscriber.error(error);
      });
      return unsubscribe;
    });
  }

  query<T>(ref: string, options?: {
    constraints?: QueryConstraint[];
  }): Observable<T[]> {
    return new Observable((subscriber) => {
      const unsubscribe = onSnapshot(query(collection(this.firestore, ref), ...(options?.constraints || [])), (querySnapshot) => {
        const data = querySnapshot.docs.map((document) => {
          return {
            ...this.changeTimestampsToDate<T>(document.data()),
            id: document.id,
          } as T;
        });
        subscriber.next(data);
      }, (error) => {
        subscriber.error(error);
      });
      return unsubscribe;
    });
  }

  update(ref: string, data: object): Promise<void> {
    return updateDoc(doc(this.firestore, ref), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  private changeTimestampsToDate<T>(obj: any): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }
    const changedObject = Object.assign({}, obj);
    const keys = Object.keys(obj);
    if (!keys.length) {
      return obj;
    }
    keys.forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        if (obj[key].constructor.name === 'Date') {
          return;
        } else if (obj[key].constructor.name === 'Array') {
          changedObject[key] = (obj[key] as unknown[]).map((o) => this.changeTimestampsToDate(o));
        } else if (obj[key].constructor.name === 'Timestamp' || obj[key].constructor.name === '_Timestamp' || typeof obj[key].toDate === 'function') {
          changedObject[key] = obj[key].toDate();
        } else if (obj[key].constructor.name === 'GeoPoint') {
          changedObject[key] = {
            latitude: obj[key].latitude,
            longitude: obj[key].longitude,
          };
        } else {
          changedObject[key] = this.changeTimestampsToDate(obj[key]);
        }
      }
    });
    return changedObject as T;
  }
}
